'use client';

import type { Address } from '@ahm/api-wrappers-core';
import { getCookie } from 'cookies-next/client';
import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import { resolveCityFromCoordinates } from '@/lib/fast-location-utils';
import { CITY_COOKIE } from '@/lib/webview-location';
import { useAuth } from '@/providers/auth-provider';
import { useAiFoodOrderStore } from '@/store';

interface StoreCitySheetContextType {
  showCityPopup: boolean;
  isNavigating: boolean;
  openCitySheet: () => void;
  closeCitySheet: () => void;
  setNavigating: (navigating: boolean) => void;
  address: Address | null;
  setAddress: (address: Address | null) => void;
}

const StoreCitySheetContext = createContext<
  StoreCitySheetContextType | undefined
>(undefined);

export function StoreCitySheetProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [showCityPopup, setShowCityPopup] = useState(false);
  const [isNavigating, setIsNavigating] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // Get loading state from auth provider to check if URL params are being processed
  const { isLoading } = useAuth();

  // Use Zustand store instead of local state
  const storeAddress = useAiFoodOrderStore.use.address();
  const setStoreAddress = useAiFoodOrderStore.use.setAddress();
  const setCity = useAiFoodOrderStore.use.setCity();
  const citySlug = useAiFoodOrderStore.use.city_slug();
  const cityId = useAiFoodOrderStore.use.city_id();
  const cityName = useAiFoodOrderStore.use.city_name();
  const requireAddressSync = useAiFoodOrderStore.use.requireAddressSync();
  const setRequireAddressSync = useAiFoodOrderStore.use.setRequireAddressSync();

  // Backfill the resolved city ({id, slug, name}) from a persisted address when
  // it's missing — e.g. a returning user whose stored data predates city_slug,
  // or a reload without ?lat/lng. Without this, city_id stays empty and the
  // (now city_id-gated) store queries never fire. Runs once per coordinate.
  const resolvedForCoordsRef = useRef<string | null>(null);
  useEffect(() => {
    if (citySlug) return; // already resolved
    if (!storeAddress?.lat || !storeAddress?.lng) return;

    const key = `${storeAddress.lat},${storeAddress.lng}`;
    if (resolvedForCoordsRef.current === key) return;
    resolvedForCoordsRef.current = key;

    void resolveCityFromCoordinates(storeAddress.lat, storeAddress.lng).then(
      (city) => {
        if (city) {
          setCity({ id: city.value, slug: city.slug, name: city.label });
        }
      }
    );
  }, [citySlug, storeAddress?.lat, storeAddress?.lng, setCity]);

  // Store (localStorage) còn city nhưng cookie city đã mất → ghi lại cookie từ
  // store, không cần gọi mạng. Cookie địa chỉ thì AddressCookieSync dựng lại,
  // cookie city không ai dựng: SSR trang rootless (`/`) đọc ra city_id rỗng và
  // phải tự phân giải lại bằng by-point ở MỌI request cho tới khi cookie có lại.
  useEffect(() => {
    if (!cityId || !citySlug) return;
    if (getCookie(CITY_COOKIE.id) && getCookie(CITY_COOKIE.slug)) return;
    setCity({ id: cityId, slug: citySlug, name: cityName ?? '' });
  }, [cityId, citySlug, cityName, setCity]);

  // Delay initial check to avoid triggering on page load
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsInitialLoad(false);
    }, 1000); // Wait 1 second after mount

    return () => clearTimeout(timer);
  }, []);

  // ĐÃ BỎ: force dialog khi city trên URL khác city của địa chỉ.
  //
  // Lệch city không còn là lỗi. URL quyết định city đang duyệt (city_id resolve
  // từ slug ở SSR), địa chỉ chỉ quyết định giao đi đâu. Vào thẳng link một city
  // khác giờ xem được nội dung ngay thay vì bị chặn bởi dialog; địa chỉ chỉ bị
  // đòi ở nơi thật sự cần toạ độ — cổng giỏ hàng (estimate/create-order).

  // Auto-check for requireAddressSync flag and open sheet if needed
  useEffect(() => {
    if (
      !isInitialLoad &&
      !isNavigating &&
      !showCityPopup && // Don't open if sheet is already open
      !isLoading && // Don't open sheet while auth-provider is loading
      requireAddressSync
    ) {
      console.log('🔄 Opening city sheet due to requireAddressSync flag');
      setShowCityPopup(true);
      // Reset the flag once we've acted on it
      setRequireAddressSync(false);
    }
  }, [
    requireAddressSync,
    isInitialLoad,
    isNavigating,
    showCityPopup,
    isLoading, // Add isLoading as dependency
    setRequireAddressSync,
  ]);

  const openCitySheet = useCallback(() => {
    // Don't open sheet if auth-provider is loading (processing URL params, login, etc.)
    if (!isNavigating && !isLoading) {
      console.log('🏠 Opening city sheet manually');
      setShowCityPopup(true);
    } else if (isLoading) {
      console.log('🏠 Skipping city sheet open - auth provider is loading');
    }
  }, [isNavigating, isLoading]);

  const closeCitySheet = useCallback(() => {
    setShowCityPopup(false);
  }, []);

  const setNavigating = useCallback((navigating: boolean) => {
    setIsNavigating(navigating);
    if (navigating) {
      setShowCityPopup(false); // Close sheet when navigating
    }
  }, []);

  const value = {
    showCityPopup,
    isNavigating,
    openCitySheet,
    closeCitySheet,
    setNavigating,
    address: storeAddress,
    setAddress: setStoreAddress,
  };

  return (
    <StoreCitySheetContext.Provider value={value}>
      {children}
    </StoreCitySheetContext.Provider>
  );
}

export function useStoreCitySheet() {
  const context = useContext(StoreCitySheetContext);
  if (context === undefined) {
    throw new Error(
      'useStoreCitySheet must be used within a StoreCitySheetProvider'
    );
  }
  return context;
}
